/**
 * Page manager section service
 * @module services/pm-section
 */
import request from 'services/request'
import {
  Map, List, Set, fromJS,
} from 'immutable'
import { createAuthorization } from '../auth'

/**
 * Get multiple page manager sections
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @return {Promise<Map>} The response body
 */
export async function getSections ({ server, auth } = {}) {
  const { body = {} } = await request
    .get(`${server}/admin/page-manager/sections`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Get one page manager section
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @param {String} options.sectionId The section id
 * @return {Promise<Map>} The response body
 */
export async function getSection ({ server, auth, sectionId } = {}) {
  const { body = {} } = await request
    .get(`${server}/admin/page-manager/section/${sectionId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Create one page multiple manager section
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @param {Object} options.data The pm-section data to create
 * @return {Promise<Map>} The response body
 */
export async function createSection ({ auth, server, data } = {}) {
  const { body = {} } = await request
    .post(`${server}/admin/page-manager/section`)
    .send(data)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Create one page multiple manager section
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @param {String} options.sectionId The section id
 * @param {Object} options.data The pm-section data to update
 * @return {Promise<Map>} The response body
 */
export async function updateSection ({
  auth, server, sectionId, data,
} = {}) {
  const { body = {} } = await request
    .put(`${server}/admin/page-manager/section/${sectionId}`)
    .send(data)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Delete one page multiple manager section
 * @param {Object} options The options
 * @param {String} options.sectionId The section id
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @return {Promise<Map>} The response body
 */
export async function deleteSection ({ auth, server, sectionId } = {}) {
  const { body = {} } = await request
    .delete(`${server}/admin/page-manager/section/${sectionId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

function getOnboardRoute (onboardVersion) {
  if (onboardVersion === '1.3') {
    return 'v5/user/onboard-choices'
  }
  return `user/onboard-choices/${onboardVersion}`
}

/**
 * Get all of the valid onboarding choices by version
 * @param {String} onboardVersion The onboarding version
 * @param {Object} options The options
 * @param {String} options.sectionId The section id
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @return {Promise<Map>} The response body as a map keyed by onboarding category
 */
export async function getOnboard (onboardVersion, { auth, server } = {}) {
  const route = getOnboardRoute(onboardVersion)
  const { body = {} } = await request
    .get(`${server}/${route}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Get topics for following vis rules
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth the jwt
 * @return {Promise<Map>} The response body as a map keyed by onboarding category
 */
export async function getTopics ({ auth, server } = {}) {
  const { body = {} } = await request
    .get(`${server}/vocabulary/61?language%5B%5D=en&p=0&pp=100&country=US`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Get all of the valid pm-list sort orders
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @return {Promise<Map>} The response body as a map with sortOrders and defaultOption
 */
export async function getListSortOrderOptions ({ auth, server } = {}) {
  const { body = {} } = await request
    .get(`${server}/admin/page-manager/list-sort-orders`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Get all of the valid section display modes
 * @param {Object} options The options
 * @param {String} options.server The server url
 * @param {Map} options.auth The server url
 * @return {Promise<Map>} The response body as a map with displayModes and defaultOption
 */
export async function getDisplayModeOptions ({ auth, server } = {}) {
  const { body = {} } = await request
    .get(`${server}/admin/page-manager/section-display-modes`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(body)
}

/**
 * Given page manager a section from state get the visibility rules
 * or an empty list
 * @param {Map} section A page manager section from state
 * @returns {List} The visibility rules or an empty List
 */
export function getSectionVisibility (section) {
  if (!Map.isMap(section)) {
    throw new Error('The section argument is requied to be an immutable Map')
  }
  return section.get('visibility') || List()
}

/**
 * Given page manager a section from state get the visibility rules
 * that are of the provided type
 * @param {Map} section A page manager section from state
 * @param {List|Set} types A collections of types
 */
export function getSectionVisibilityRulesByType (section, types) {
  if (!Map.isMap(section)) {
    throw new Error('The section argument is requied to be an immutable Map')
  }
  if (!List.isList(types) && !Set.isSet(types)) {
    throw new Error('The types argument is requied to be an immutable List')
  }
  const visibility = getSectionVisibility(section)
  return visibility.filter((rule) => types.includes(rule.get('type')))
}
